/**
 * Tour phase definitions.
 *
 * Each phase is a group of driver.js steps that run on a specific page.
 * Phases execute sequentially — when one finishes, the next begins after
 * any required setup (navigating, showing the demo row, etc.).
 *
 * Visits /properties, the first property in the investor's list (the demo
 * property if the list is empty), /offers and /buybox. When the nav is
 * collapsed, nav steps open the mobile drawer and highlight the link in it.
 * Steps whose element isn't on the page are dropped at runtime.
 */

import type { DriveStep } from 'driver.js'

// =============================================================================
// Phase IDs
// =============================================================================

export type TourPhaseId =
  | 'browse'
  | 'property-table'
  | 'property-detail'
  | 'offers'
  | 'buyboxes'
  | 'buybox-form'
  | 'settings'
  | 'complete'

export const PHASE_ORDER: TourPhaseId[] = [
  'browse',
  'property-table',
  'property-detail',
  'offers',
  'buyboxes',
  'buybox-form',
  'settings',
  'complete',
]

// =============================================================================
// Element finders
// =============================================================================

// The property the tour opens, so step 2 highlights that exact row
let tourPropertyId: string | null = null

export function setTourPropertyId(id: string | null): void {
  tourPropertyId = id
}

/** The row for the property the tour opens, else the demo row (empty list). */
export function findTourPropertyRow(): Element | null {
  return tourPropertyId
    ? document.querySelector(`[data-row-key="${tourPropertyId}"]`)
    : document.querySelector('[data-tour-step="demo-property-card"]')
}

/** Phases whose step highlights a nav link (inside the drawer when collapsed). */
export const NAV_PHASES: TourPhaseId[] = ['browse', 'offers', 'settings']

/** Whether the nav is collapsed into the hamburger drawer (its breakpoint differs from the tour's). */
export function isNavCollapsed(): boolean {
  const el = document.querySelector('[data-tour-step="mobile-nav"]') as HTMLElement | null
  return !!el && el.offsetParent !== null
}

/** The desktop nav link, or its copy inside the open mobile drawer. */
function navTarget(link: string): () => Element {
  return () =>
    document.querySelector(`[data-tour-step="${isNavCollapsed() ? `${link}-drawer` : link}"]`) ??
    document.body
}

/** The investor's most recently created buybox card on the page. */
export function findNewestBuyboxCard(): Element | null {
  const cards = Array.from(document.querySelectorAll('[data-tour-step="buybox-card"]'))
  if (cards.length === 0) return null
  return cards.reduce((newest, card) =>
    (card.getAttribute('data-created-at') ?? '') > (newest.getAttribute('data-created-at') ?? '')
      ? card
      : newest
  )
}

// =============================================================================
// Desktop step definitions
// =============================================================================

const BROWSE_STEPS: DriveStep[] = [
  {
    element: navTarget('browse-properties'),
    popover: {
      title: 'Browse Properties',
      description:
        'Off-market deals land here the moment they go live. Sort and filter to find properties that fit your strategy.',
      side: 'bottom',
      align: 'start',
    },
  },
]

const PROPERTY_TABLE_STEPS: DriveStep[] = [
  {
    element: () => findTourPropertyRow() ?? document.body,
    popover: {
      title: 'Properties',
      description:
        "View estimated value, condition and offer deadline at a glance. Let's open this one.",
      side: 'bottom',
      align: 'start',
    },
  },
]

const PROPERTY_DETAIL_STEPS: DriveStep[] = [
  {
    element: '[data-tour-step="property-info"]',
    popover: {
      title: 'Property Details',
      description:
        "See photos and every detail the seller's agent shared about this property.",
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour-step="property-actions"]',
    popover: {
      title: 'Make an Offer',
      description:
        "Submit your offer here, or ask the seller's agent a question to get the conversation going.",
      side: 'left',
      align: 'start',
    },
  },
]

/** Step 4 when "Submit offer" is hidden (the investor already has an offer). */
const ASK_ONLY_POPOVER = {
  title: 'Ask a Question',
  description: "Message the listing agent directly. They're notified right away.",
}

const OFFERS_STEPS: DriveStep[] = [
  {
    element: navTarget('my-offers'),
    popover: {
      title: 'My Offers',
      description: 'Once you submit an offer, track it here from Pending to Accepted.',
      side: 'bottom',
      align: 'start',
    },
  },
]

// Without a buybox, the card and edit steps drop out (no card on the page)
const BUYBOXES_STEPS: DriveStep[] = [
  {
    element: () => findNewestBuyboxCard() ?? document.body,
    popover: {
      title: 'Your Buybox',
      description:
        "This is the buybox you just created. We'll email new matches on the schedule shown here.",
      side: 'right',
      align: 'start',
    },
  },
  {
    element: () =>
      findNewestBuyboxCard()?.querySelector('[data-tour-step="buybox-edit"]') ?? document.body,
    popover: {
      title: 'Edit & Auto-Bid',
      description:
        'Fine-tune your criteria and alerts, or turn on auto-bid to offer on matches automatically.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-tour-step="buybox-create"]',
    popover: {
      title: 'New Buybox',
      description: 'Running more than one strategy? Create a separate buybox for each.',
      side: 'bottom',
      align: 'end',
    },
  },
]

/** Step 6 when the investor has no buybox yet (they skipped it in onboarding). */
const CREATE_FIRST_BUYBOX_POPOVER = {
  title: 'Create a Buybox',
  description:
    "Get email alerts when off-market deals that fit your strategy go live. Let's take a look.",
}

/** No-buybox path only: a view-only look at the Create Buybox form. */
const BUYBOX_FORM_STEPS: DriveStep[] = [
  {
    element: '[data-tour-step="buybox-criteria"]',
    popover: {
      title: 'Your Criteria',
      description:
        "Choose your preferred market, AVM range and condition. We'll email you new deals that match.",
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour-step="buybox-auto-bid"]',
    popover: {
      title: 'Auto-Bid (Optional)',
      description: 'Turn this on to offer automatically on properties that match your criteria.',
      side: 'right',
      align: 'start',
    },
  },
]

const SETTINGS_STEPS: DriveStep[] = [
  {
    element: navTarget('settings'),
    popover: {
      title: 'Settings',
      description: 'Update your profile and notifications, or replay this tour anytime.',
      side: 'bottom',
      align: 'end',
    },
  },
]

const COMPLETE_STEPS: DriveStep[] = [
  {
    popover: {
      title: "You're All Set!",
      description: "Start browsing below. We'll email you when new properties match your buybox.",
    },
  },
]

/** Final step when the investor has no buybox yet. */
const COMPLETE_NO_BUYBOX_POPOVER = {
  title: "You're All Set!",
  description: 'Start browsing below. Create a buybox anytime to get new matches by email.',
}

// =============================================================================
// Mobile step overrides
// =============================================================================

/** Mobile: no room beside the buttons, so step 4 sits directly above them. */
const MOBILE_PROPERTY_DETAIL_STEPS: DriveStep[] = PROPERTY_DETAIL_STEPS.map((step) =>
  step.element === '[data-tour-step="property-actions"]'
    ? { ...step, popover: { ...step.popover, side: 'top', align: 'center' } }
    : step
)

// =============================================================================
// Phase maps — desktop and mobile
// =============================================================================

export const PHASE_STEPS: Record<TourPhaseId, DriveStep[]> = {
  'browse': BROWSE_STEPS,
  'property-table': PROPERTY_TABLE_STEPS,
  'property-detail': PROPERTY_DETAIL_STEPS,
  'offers': OFFERS_STEPS,
  'buyboxes': BUYBOXES_STEPS,
  'buybox-form': BUYBOX_FORM_STEPS,
  'settings': SETTINGS_STEPS,
  'complete': COMPLETE_STEPS,
}

/** Mobile: step 4 placement only; nav steps pick their target at runtime. */
export const MOBILE_PHASE_STEPS: Record<TourPhaseId, DriveStep[]> = {
  ...PHASE_STEPS,
  'property-detail': MOBILE_PROPERTY_DETAIL_STEPS,
}

// =============================================================================
// Runtime step resolution
// =============================================================================

function isVisible(el: Element | null): boolean {
  return !!el && (el as HTMLElement).offsetParent !== null
}

/** Resolve a step's target element, or null if it has none. */
function resolveElement(step: DriveStep): Element | null {
  const { element } = step
  if (!element) return null
  if (typeof element === 'string') return document.querySelector(element)
  const el = typeof element === 'function' ? element() : element
  return el === document.body ? null : el
}

/** Alternate copy for a step, or null to keep its default. */
function popoverOverride(
  step: DriveStep,
  hasBuybox: boolean
): Partial<NonNullable<DriveStep['popover']>> | null {
  if (
    step.element === '[data-tour-step="property-actions"]' &&
    !isVisible(document.querySelector('[data-tour-step="submit-offer"]'))
  ) {
    return ASK_ONLY_POPOVER
  }
  if (!hasBuybox && step.element === '[data-tour-step="buybox-create"]') {
    return CREATE_FIRST_BUYBOX_POPOVER
  }
  if (!hasBuybox && COMPLETE_STEPS.includes(step)) return COMPLETE_NO_BUYBOX_POPOVER
  return null
}

/**
 * The steps to actually show for a phase, given what's on the page now:
 * drops steps whose element is missing or hidden, and swaps in the
 * ask-only and no-buybox copy where it applies.
 */
export function resolvePhaseSteps(
  phase: TourPhaseId,
  mobile: boolean,
  hasBuybox: boolean
): DriveStep[] {
  if (phase === 'buybox-form' && hasBuybox) return []
  const steps = (mobile ? MOBILE_PHASE_STEPS : PHASE_STEPS)[phase] ?? []
  const inDrawer = NAV_PHASES.includes(phase) && isNavCollapsed()
  return steps
    .map((step) =>
      inDrawer ? { ...step, popover: { ...step.popover, popoverClass: 'tour-popover-drawer' } } : step
    )
    .filter((step) => !step.element || isVisible(resolveElement(step)))
    .map((step) => {
      const override = popoverOverride(step, hasBuybox)
      return override ? { ...step, popover: { ...step.popover, ...override } } : step
    })
}

/** Steps in the phases after `phase`, assuming each shows in full. */
export function countRemainingSteps(
  phase: TourPhaseId,
  mobile: boolean,
  hasBuybox: boolean
): number {
  const steps = mobile ? MOBILE_PHASE_STEPS : PHASE_STEPS
  return PHASE_ORDER.slice(PHASE_ORDER.indexOf(phase) + 1).reduce((sum, p) => {
    // Without a buybox only the Create Buybox step shows on that page,
    // followed by the form walkthrough; with one, the form is skipped
    if (p === 'buyboxes' && !hasBuybox) return sum + 1
    if (p === 'buybox-form' && hasBuybox) return sum
    return sum + (steps[p]?.length ?? 0)
  }, 0)
}

/** Get the next phase ID, or null if this is the last phase. */
export function getNextPhase(current: TourPhaseId): TourPhaseId | null {
  const idx = PHASE_ORDER.indexOf(current)
  if (idx === -1 || idx === PHASE_ORDER.length - 1) return null
  return PHASE_ORDER[idx + 1]
}

/** Get the previous phase ID, or null if this is the first phase. */
export function getPrevPhase(current: TourPhaseId): TourPhaseId | null {
  const idx = PHASE_ORDER.indexOf(current)
  return idx > 0 ? PHASE_ORDER[idx - 1] : null
}
